import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import type { EmptyProps } from '@change-corgi/core/types';

import type { SessionUser } from '#src/shared/session';

export type LoginModalOptions = {
	user?: SessionUser;
	email: string;
	showContinueAsGuest: boolean;
	googleAuthEnabled: boolean;
};

export type LoginModalResult = { user: SessionUser | undefined; continueAsGuest?: boolean };

export type EmailVerificationModalOptions = {
	path: string;
	showContinueAsGuest: boolean;
	userId: string;
};

export type ModalType = 'highAmount' | 'overwritePaymentMethod' | 'login' | 'emailVerification';

export type ModalOptions =
	| { type: 'highAmount' }
	| { type: 'overwritePaymentMethod' }
	| ({ type: 'login' } & LoginModalOptions)
	| ({ type: 'emailVerification' } & EmailVerificationModalOptions);

export type ModalResult<T extends ModalType = ModalType> = T extends 'login'
	? LoginModalResult | undefined
	: T extends 'emailVerification'
		? undefined
		: boolean;

type ResolveCallback = (result: ModalResult | PromiseLike<ModalResult>) => void;

type DisplayModal = {
	(options: { type: 'highAmount' }): Promise<boolean>;
	(options: { type: 'overwritePaymentMethod' }): Promise<boolean>;
	(options: { type: 'login' } & LoginModalOptions): Promise<LoginModalResult | undefined>;
	(options: { type: 'emailVerification' } & EmailVerificationModalOptions): Promise<void>;
};

type HandleModalResult = (result: ModalResult) => void;

export type ModalConfirmationContextValue = [
	{
		modalOptions: ModalOptions | null;
	},
	{
		displayModal: DisplayModal;
		handleModalResult: HandleModalResult;
	},
];

export const ModalConfirmationContext = createContext<ModalConfirmationContextValue | null>(null);

export const ModalConfirmationProvider: React.FC<React.PropsWithChildren<EmptyProps>> = ({ children }) => {
	const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);
	const resolveRef = useRef<ResolveCallback | null>(null);

	const displayModal = useCallback(async (options: ModalOptions) => {
		setModalOptions(options);
		// eslint-disable-next-line promise/avoid-new
		return new Promise<ModalResult>((resolve) => {
			resolveRef.current = resolve;
		});
		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
	}, []) as DisplayModal;

	const handleModalResult = useCallback((result: ModalResult) => {
		setModalOptions(null);
		if (resolveRef.current) {
			resolveRef.current(result);
			resolveRef.current = null;
		}
	}, []);

	return (
		<ModalConfirmationContext.Provider
			value={[
				{
					modalOptions,
				},
				{
					displayModal,
					handleModalResult,
				},
			]}
		>
			{children}
		</ModalConfirmationContext.Provider>
	);
};

export function useModalConfirmationContext(): ModalConfirmationContextValue {
	const context = useContext(ModalConfirmationContext);

	if (!context) {
		throw new Error('useModalConfirmationContext must be used within ModalConfirmationProvider');
	}

	return context;
}
